import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiFilm } = FiIcons;

const Portfolio = () => {
  const [failedImages, setFailedImages] = useState(new Set());

  const projects = [
    {
      id: 'tinman',
      title: 'Tinman',
      category: 'Sci-Fi Feature',
      description: 'A dedicated surgeon puzzles with the idea that a world wide virus is man made.',
      path: '/tinman',
      image: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?auto=format&fit=crop&w=1200&q=80'
    },
    {
      id: 'playground',
      title: 'The Playground',
      category: 'Horror Thriller',
      description: 'Where innocence meets terror. A haunting psychological thriller.',
      path: '/playground',
      image: 'https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=1200&q=80'
    },
    {
      id: 'festivals',
      title: 'The Festivals',
      category: 'Awards & Press',
      description: 'Celebrating cinematic achievements across the globe.',
      path: '/festivals',
      image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=80'
    },
    {
      id: 'fairytales',
      title: 'Truth & Fairytales',
      category: 'Dramatic Series',
      description: 'A raw deconstruction of modern grief and survival.',
      path: '/fairytales',
      image: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80'
    },
    {
      id: 'shorts',
      title: 'YouTube Shorts',
      category: 'Digital Content',
      description: 'Visionary micro-narratives curated for the digital age.',
      path: '/shorts',
      image: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80'
    },
    {
      id: 'print',
      title: 'In Print',
      category: 'Media Coverage',
      description: 'Sandstorm Flix featured in leading publications.',
      path: '/print',
      image: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=1200&q=80'
    }
  ];

  const handleImageError = (id) => {
    setFailedImages(prev => new Set([...prev, id]));
  };

  const visibleProjects = useMemo(() => 
    projects.filter(p => !failedImages.has(p.id)), 
  [failedImages]);

  return (
    <section className="py-24 bg-white overflow-hidden" id="showcase">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-16"
        >
          <span className="text-accent-orange font-bold tracking-[0.4em] uppercase text-[10px] block mb-4 italic">Portfolio</span>
          <h2 className="text-5xl md:text-7xl font-serif font-bold text-black uppercase tracking-tight">What projects can you explore?</h2>
          <div className="w-20 h-1 bg-accent-orange mx-auto mt-6"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {visibleProjects.map((project, idx) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="group relative"
              >
                <Link to={project.path} className="block relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-beige-light border border-beige group-hover:shadow-2xl transition-all duration-700">
                  <img
                    src={project.image}
                    alt={`${project.title} — ${project.category}`}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000 rounded-[2rem]"
                    onError={() => handleImageError(project.id)}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500 rounded-[2rem]"></div>
                  
                  <div className="absolute inset-0 p-8 flex flex-col justify-end">
                    <div className="translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <span className="text-accent-orange font-bold text-[10px] tracking-[0.3em] uppercase mb-2 block">{project.category}</span>
                      <h3 className="text-3xl font-serif font-bold text-white uppercase tracking-tight mb-2">{project.title}</h3>
                      <p className="text-white/60 text-[11px] font-medium leading-relaxed mb-4 group-hover:text-white/80 transition-colors line-clamp-2 italic">
                        {project.description}
                      </p>
                      <div className="flex items-center gap-2 text-white/60 group-hover:text-white transition-colors text-[10px] font-bold uppercase tracking-widest">
                        Explore Project <SafeIcon icon={FiArrowRight} className="w-3 h-3 group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </div>

                  <div className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all duration-500 scale-50 group-hover:scale-100">
                    <SafeIcon icon={FiFilm} className="w-4 h-4" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;